<template lang="pug">
#waiter-detail(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            b-spinner.spinner.mr-2(v-if="waiter == null")
            span(v-else-if="waiter.name") {{ waiter.name }}
            span(v-else).warning-message 未入力
            span 様の詳細
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.notification-btn(type="button" @click="onBack") 戻る
    .box-body
      b-tabs(v-model="tabIndex")
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 申込内容・登録情報
          WaiterDetailTab(ref="waiterDetailTab" @onChangeRequest="gotoChangeRequest" @onWaiterEdit="gotoWaiterEdit")
        b-tab.tab(v-if="_isNotTemporarily && _isOverWaiterBasicPlan")
          template(v-slot:title)
            .tab-title
              span 入所評価判定
          JudgementWaiterTab(ref="judgementWaiterTab" @onUpdate="updateWaiterJudgement")
        b-tab.tab(v-if="_isNotTemporarily && _isOverWaiterBasicPlan")
          template(v-slot:title)
            .tab-title
              span 経過記録
          WaiterProgressHistoriesTab(ref="waiterProgressHistoriesTab" @onDelete="deleteWaiterProgressHistory")
        b-tab.tab(v-if="_isMineFacility && _isNotTemporarily")
          template(v-slot:title)
            .tab-title
              span 家族ユーザー
          WaiterFamilyListTab(ref="waiterFamilyListTab"
            @onDeleteAllFamily="deleteAllFamily" @onDeleteLineFamily="deleteLineFamily" @onDeleteMailFamily="deleteMailFamily")
  
  OkModal(v-model="showSuccessModal" :title="successModalTitle" @ok="refresh")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { WaiterListState, SendWaiterMessagesState } from "@/store/NeedRefreshes";
import { tabIndexesStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";

import WaitersService, { WaitersGetModel, WaitersPatchModel } from "@/commons/services/Waiters";
import WaiterProgressHistoriesService, { WaiterProgressHistoriesGetModel } from "@/commons/services/WaiterProgressHistories";
import FamiliesService, { FamilyDeleteModel, SenderType } from "@/commons/services/Families";
import MailFamiliesService from "@/commons/services/MailFamilies";
import { WaiterServicePlanType } from "@/commons/services/WaiterServicePlans";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import WaiterDetailTab from "@/pc/containers/waiters/WaiterDetailTab.vue";
import JudgementWaiterTab from "@/pc/containers/waiters/JudgementWaiterTab.vue";
import WaiterProgressHistoriesTab from "@/pc/containers/waiters/WaiterProgressHistoriesTab.vue";
import WaiterFamilyListTab from "@/pc/containers/waiters/WaiterFamilyListTab.vue";
import { ResidentStatus } from "@/commons/services/Residents";
import { WaiterFamilyTableItem } from "@/pc/containers/waiters/WaiterFamilyTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 待機者一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal,
    WaiterDetailTab,
    JudgementWaiterTab,
    WaiterProgressHistoriesTab,
    WaiterFamilyListTab
  }
})
export default class WaiterDetail extends Vue {
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** 待機者経過履歴サービス */
  @Inject("WaiterProgressHistoriesService") waiterProgressHistoriesService!: WaiterProgressHistoriesService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 家族メールユーザーサービス */
  @Inject("MailFamiliesService") mailFamiliesService!: MailFamiliesService;

  @Ref() waiterDetailTab!: WaiterDetailTab;
  @Ref() judgementWaiterTab!: JudgementWaiterTab;
  @Ref() waiterProgressHistoriesTab!: WaiterProgressHistoriesTab;
  @Ref() waiterFamilyListTab!: WaiterFamilyListTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 完了モーダル表示可否 */
  showSuccessModal: boolean = false;
  /** 完了モーダルタイトル */
  successModalTitle: string = "";

  /** 待機者 */
  waiter: WaitersGetModel | null = null;

  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;

  /** ### プラン(待機者)がベーシック以上かを返す */
  get _isOverWaiterBasicPlan(): boolean {
    const planType: WaiterServicePlanType = userStore.facility.waiterServicePlanType;

    if (WaiterServicePlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### vue lifecycle mounted
   *
   * - タブ番号を復元後、初期値に戻す
   * - 画面読込みは非同期で行われるので、完了を待ってからタブ番号を復元する
   */
  public mounted(): void {
    this.$nextTick(() => {
      this.tabIndex = tabIndexesStore.waiterDetailTabIndex;
      tabIndexesStore.setWaiterDetailTabIndex({ index: 0 });
    });

    this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    const idinsystem: number = Number(this.$route.params.idinsystem);
    const waiter: WaitersGetModel = await this.getWaiter(facilityId, residentId, idinsystem);
    this.waiter = waiter;

    return;
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiter(facilityId: number, residentId: number, idinsystem: number): Promise<WaitersGetModel> {
    const waitersByIdInSystem: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, idinsystem);
    for (const waiter of waitersByIdInSystem) {
      if (waiter.facilityId == facilityId) {
        return waiter;
      }
    }
    // 見つからない場合は個別取得する
    const waiter: WaitersGetModel = await this.waitersService.get(userStore, facilityId, residentId);
    return waiter;
  }

  /**
   * ### 一時保存以外かどうか
   */
  get _isNotTemporarily(): boolean {
    if (this.waiter == null) {
      return false;
    }

    if (this.waiter.status == ResidentStatus.TEMPORARILY) {
      return false;
    }

    return true;
  }

  /**
   * ### 自施設かどうか
   */
  get _isMineFacility(): boolean {
    const facilityId: number = Number(this.$route.params.facilityid);
    if (userStore.loginUser.facilityId == facilityId) {
      return true;
    } else {
      return false;
    }
  }

  public onBack(): void {
    this.$router.push(PcUrl.WaiterList);
    return;
  }

  /**
   * ### 待機者変更申請画面へ遷移する
   */
  public gotoChangeRequest(): void {
    tabIndexesStore.setWaiterDetailTabIndex({ index: 0 });
    this.$router.push({
      name: PcUrl.WaiterChangeRequest,
      params: { facilityid: this.$route.params.facilityid, residentid: this.$route.params.residentid, idinsystem: this.$route.params.idinsystem }
    });
    return;
  }

  /**
   * ### 待機者編集画面へ遷移する
   */
  public gotoWaiterEdit(): void {
    tabIndexesStore.setWaiterDetailTabIndex({ index: 0 });
    this.$router.push({
      name: PcUrl.WaiterEdit,
      params: { facilityid: this.$route.params.facilityid, residentid: this.$route.params.residentid, idinsystem: this.$route.params.idinsystem, backtype: "2" }
    });
    return;
  }

  /**
   * ### 待機者の判定内容を更新する
   */
  public async updateWaiterJudgement(requestBody: WaitersPatchModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    await this.waitersService.patch(userStore, facilityId, residentId, requestBody);

    // 待機者一覧を再読み込みさせるようstoreを更新
    let waiterList: WaiterListState = needRefreshesStore.waiterList;
    waiterList.waiterList = true;
    needRefreshesStore.setWaiterList(waiterList);
    // メッセージ送信(待機者)の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
    sendWaiterMessageState.sendMessages = true;
    needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

    this.successModalTitle = "変更に成功しました。";
    this.showSuccessModal = true;
    this.isBusy = false;

    this.refresh();

    return;
  }

  /**
   * ### 待機者の経過記録を削除する
   */
  public async deleteWaiterProgressHistory(history: WaiterProgressHistoriesGetModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.waiterProgressHistoriesService.delete(userStore, history.residentIdInSystem, history.id);

    this.successModalTitle = "削除に成功しました。";
    this.showSuccessModal = true;
    this.isBusy = false;

    this.refresh();

    return;
  }

  /**
   * ### 全家族ユーザーを削除する
   */
  public async deleteAllFamily(event: {
    residentId: number;
    families: Array<WaiterFamilyTableItem>;
    concurrentDeleteFacilityIds: number[];
    deleteMessage: string;
    deleteModalMessageTitle: string;
    deleteModalMessageText: string;
  }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    for (const family of event.families) {
      if (family.senderType == SenderType.LINE) {
        const requestBody: FamilyDeleteModel = {
          deleteMessageTitle: event.deleteModalMessageTitle,
          deleteMessageText: event.deleteModalMessageText,
          concurrentDeleteFacilityIds: event.concurrentDeleteFacilityIds
        };
        await this.familiesService.delete(userStore, event.residentId, family.familyId, requestBody);
      } else if (family.senderType == SenderType.MAIL) {
        await this.mailFamiliesService.delete(userStore, event.residentId, family.familyId, { concurrentDeleteFacilityIds: event.concurrentDeleteFacilityIds });
      }
    }

    this.successModalTitle = "削除に成功しました。";
    this.showSuccessModal = true;
    this.isBusy = false;

    this.refresh();

    return;
  }

  /**
   * ### 家族ユーザー(LINE)を削除する
   */
  public async deleteLineFamily(event: {
    residentId: number;
    familyId: number;
    concurrentDeleteFacilityIds: number[];
    deleteModalMessageTitle: string;
    deleteModalMessageText: string;
  }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const requestBody: FamilyDeleteModel = {
      deleteMessageTitle: event.deleteModalMessageTitle,
      deleteMessageText: event.deleteModalMessageText,
      concurrentDeleteFacilityIds: event.concurrentDeleteFacilityIds
    };
    await this.familiesService.delete(userStore, event.residentId, event.familyId, requestBody);

    this.successModalTitle = "削除に成功しました。";
    this.showSuccessModal = true;
    this.isBusy = false;

    this.refresh();

    return;
  }

  /**
   * ### 家族ユーザー(メール)を削除する
   */
  public async deleteMailFamily(event: { residentId: number; familyId: number; concurrentDeleteFacilityIds: number[] }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.mailFamiliesService.delete(userStore, event.residentId, event.familyId, { concurrentDeleteFacilityIds: event.concurrentDeleteFacilityIds });

    this.successModalTitle = "削除に成功しました。";
    this.showSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 各タブの表示情報を更新する
   */
  public refresh(): void {
    this.judgementWaiterTab.refresh();
    this.waiterProgressHistoriesTab.refresh();
    // 家族タブは表示している場合のみrefreshする
    if (this._isMineFacility && this._isNotTemporarily) {
      this.waiterFamilyListTab.refresh();
    }
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      if (this._isNotTemporarily && this._isOverWaiterBasicPlan) {
        this.waiterProgressHistoriesTab.setFilterSetting();
      }
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#waiter-detail {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .spinner {
      width: 22px;
      height: 22px;
    }
  }
}
</style>
